import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from "recharts";

interface DataPoint {
  label: string;
  [key: string]: any; // Flexible data structure
  isReference?: boolean;
  referenceLabel?: string;
}

interface DoubleLineChartProps {
  data: DataPoint[];
  dataKey1?: string; // Key for the first line data
  dataKey2?: string; // Key for the second line data
  height?: number | string;
  width?: number | string;
  line1Color?: string;
  line2Color?: string;
  referenceLineColor?: string;
  referenceLabelColor?: string;
  referenceLabelBgColor?: string;
  strokeWidth?: number;
  backgroundColor?: string;
  gridStrokeColor?: string;
  dotFillColor?: string;
  cursorColor?: string;
  series_labels?: string[]; // Improved type definition
  xAxisTitle?: string; // Added x-axis title prop
  yAxisTitle?: string; // Added y-axis title prop
}

const DoubleLineChart: React.FC<DoubleLineChartProps> = ({
  data,
  dataKey1 = "value1",
  dataKey2 = "value2",
  height = "100%",
  width = "100%",
  line1Color = "#04BFDA",
  line2Color = "#5187FE", // Default orange color for second line
  referenceLineColor = "rgba(255, 255, 255, 0.5)",
  referenceLabelColor = "#FFFFFF",
  strokeWidth = 2,
  gridStrokeColor = "rgba(255, 255, 255, 0.3)",
  dotFillColor = "#000",
  cursorColor = "#A4A8AE",
  series_labels = ["line1", "line2"], // Default series labels
  xAxisTitle,
  yAxisTitle,
}) => {
  // Process the data
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  // Calculate evenly spaced Y-axis ticks based on both lines
  const calculateYAxisTicks = () => {
    if (!processedData || processedData.length === 0) return [];

    const values1 = processedData.map((item) => item[dataKey1] || 0);
    const values2 = processedData.map((item) => item[dataKey2] || 0);
    const allValues = [...values1, ...values2];

    // Find the actual min and max
    const actualMin = Math.min(...allValues);
    const actualMax = Math.max(...allValues);

    // Calculate a nice interval for the data range
    // Target around 5-7 ticks for most charts
    const targetTickCount = 5;
    const rawRange = actualMax - actualMin;

    // Calculate a nice round interval value
    // Start with a raw estimation
    const roughInterval = rawRange / targetTickCount;

    // Convert to a nice round number (1, 2, 5, 10, 20, 50, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
    const normalizedInterval = roughInterval / magnitude; // Between 1 and 10

    let niceInterval;
    if (normalizedInterval < 1.5) {
      niceInterval = 1;
    } else if (normalizedInterval < 3) {
      niceInterval = 2;
    } else if (normalizedInterval < 7) {
      niceInterval = 5;
    } else {
      niceInterval = 10;
    }
    niceInterval *= magnitude;

    // Round the min value down to the nearest interval
    const niceMin = Math.floor(actualMin / niceInterval) * niceInterval;
    
    // Calculate the nice max by finding the next tick above the actual max
    const niceMax = Math.ceil(actualMax / niceInterval) * niceInterval;
    
    // Ensure we have at least one tick above the max value
    const guaranteedMax = niceMax < actualMax ? niceMax + niceInterval : niceMax;

    // Generate evenly spaced ticks
    const ticks = [];
    let currentTick = niceMin;

    // Add ticks until we reach or exceed the guaranteed max
    while (currentTick <= guaranteedMax) {
      ticks.push(currentTick);
      currentTick += niceInterval;
    }

    return ticks;
  };

  // Find reference point from processed data
  const referencePoint = processedData.find((point) => point.isReference);

  // Generate the y-axis ticks
  const yAxisTicks = calculateYAxisTicks();

  // Custom dot component that only renders for reference points
  const CustomDot = (props: any) => {
    const { cx, cy, payload, dataKey } = props;

    // Only render the dot if this is a reference point
    if (payload.isReference) {
      const color = dataKey === dataKey1 ? line1Color : line2Color;

      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          stroke={color}
          strokeWidth={2}
          fill={dotFillColor}
        />
      );
    }
    // Don't render dots for other points
    return null;
  };

  // Custom cursor for the tooltip
  const CustomCursor = (props: any) => {
    const { points, height } = props;
    const { x, y } = points[0];

    // Calculate spacing for dash segments
    const dashLength = 3;
    const gapLength = 3;
    const segmentLength = dashLength + gapLength;
    const totalHeight = height;
    const numSegments = Math.floor(totalHeight / segmentLength);

    return (
      <g>
        {Array.from({ length: numSegments }).map((_, index) => (
          <line
            key={index}
            x1={x}
            y1={y + index * segmentLength}
            x2={x}
            y2={y + index * segmentLength + dashLength}
            stroke={cursorColor}
            strokeWidth={1}
            opacity={0.9}
          />
        ))}
      </g>
    );
  };

  // Custom active dot component for hover indicator
  const CustomActiveDot = (props: any) => {
    const { cx, cy, dataKey } = props;
    const color = dataKey === dataKey1 ? line1Color : line2Color;

    return (
      <g>
        {/* Outer circle */}
        <circle cx={cx} cy={cy} r={5} fill={color} />
        {/* Inner circle */}
        <circle cx={cx} cy={cy} r={2.5} fill="#FFFFFF" />
      </g>
    );
  };

  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart
        data={processedData}
        margin={{ top: 30, right: 20, left: 10, bottom: 20 }}
      >
        <defs>
          {/* Gradient for first line */}
          <linearGradient id="color1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={line1Color} stopOpacity={0.8} />
            <stop offset="50%" stopColor={line1Color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={line1Color} stopOpacity={0.05} />
          </linearGradient>

          {/* Gradient for second line */}
          <linearGradient id="color2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={line2Color} stopOpacity={0.8} />
            <stop offset="50%" stopColor={line2Color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={line2Color} stopOpacity={0.05} />
          </linearGradient>
        </defs>

        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={gridStrokeColor}
          horizontal={true}
        />

        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          tick={{ fill: "rgba(255, 255, 255, 0.8)", fontSize: xAxisTitle ? 14 : 12 }}
          stroke="rgba(255, 255, 255, 0.5)"
          padding={{ left: 10, right: 10 }}
          height={xAxisTitle ? 80 : 40}
          dy={10}
          label={xAxisTitle ? {
            value: xAxisTitle,
            position: "insideBottom",
            offset: 0,
            fill: "rgba(255, 255, 255)",
            fontSize: 16,
            fontWeight: 500,
            textAnchor: "middle",
          } : undefined}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: "rgba(255, 255, 255, 0.8)", fontSize: yAxisTitle ? 14 : 12 }}
          stroke="rgba(255, 255, 255, 0.5)"
          scale="linear"
          {...(yAxisTitle && { width: 100 })}
          dx={-5}
          ticks={yAxisTicks}
          domain={
            yAxisTicks.length > 0
              ? [yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]
              : [0, "auto"]
          }
          label={yAxisTitle ? {
            value: yAxisTitle,
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: 'middle' },
            fill: "rgba(255, 255, 255)",
            fontSize: 16,
            fontWeight: 500,
            offset: 30,
          } : undefined}
        />

        <Tooltip
          contentStyle={{
            backgroundColor: "#363441",
            borderColor: "#363441",
            outline: "none !important",
            color: "white",
            padding: "4px 8px",
            fontSize: "10px",
            minWidth: "auto",
            borderRadius: "5px",
          }}
          labelStyle={{ display: "none" }}
          itemStyle={{
            color: "white",
            fontSize: "10px",
            padding: "2px 0",
            fontWeight: 600,
          }}
          formatter={(value, name) => {
            let displayName = name;
            if (name === dataKey1) {
              displayName = series_labels[0] || name;
            } else if (name === dataKey2) {
              displayName = series_labels[1] || name;
            }
            return [`${value}`, displayName];
          }}
          cursor={<CustomCursor />}
        />

        {referencePoint && (
          <ReferenceLine
            x={referencePoint.label}
            stroke={referenceLineColor}
            strokeDasharray="3 3"
          >
            <Label
              value={referencePoint.referenceLabel}
              position="top"
              fill={referenceLabelColor}
              style={{
                fontSize: "12px",
                fontWeight: 500,
              }}
              offset={10}
            />
          </ReferenceLine>
        )}

        {/* First Area */}
        <Area
          type="monotone"
          dataKey={dataKey1}
          stroke={line1Color}
          strokeWidth={strokeWidth}
          fill="url(#color1)"
          dot={<CustomDot />}
          activeDot={<CustomActiveDot />}
        />

        {/* Second Area */}
        <Area
          type="monotone"
          dataKey={dataKey2}
          stroke={line2Color}
          strokeWidth={strokeWidth}
          fill="url(#color2)"
          dot={<CustomDot />}
          activeDot={<CustomActiveDot />}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default DoubleLineChart;
